import React, { useState, useMemo, useEffect, Suspense, lazy } from 'react';
import { Helmet } from 'react-helmet';
import { AlertCircle, Users, TrendingDown, Clock, ArrowRight, CheckCircle2, Zap, TrendingUp, Truck, Edit3, Save, X } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { handleWhatsAppClick } from '@/lib/whatsapp.js';

// Context
import { useEditContext } from '@/contexts/EditContext.jsx';

// Custom Components
import SegmentCard from '@/components/SegmentCard.jsx';
import ComparisonRow from '@/components/ComparisonRow.jsx';
import ProductCard from '@/components/ProductCard.jsx';
import Footer from '@/components/Footer.jsx';
import Header from '@/components/Header.jsx';
import WhatsAppIcon from '@/components/WhatsAppIcon.jsx';
import EditableField from '@/components/EditableField.jsx';
import HeroSection from '@/components/HeroSection.jsx';
import SolutionsSection from '@/components/SolutionsSection.jsx';
import TrustProofSection from '@/components/TrustProofSection.jsx';
import WhatsAppButton from '@/components/WhatsAppButton.jsx';
import StatsSection from '@/components/StatsSection.jsx';
import produtoPaoFrances from '@/assets/produto-pao-frances.webp';
import produtoPaoDeQueijo from '@/assets/produto-pao-de-queijo.webp';
import produtoDocesFinos from '@/assets/produto-doces-finos.webp';

const ProfitMarginCalculator = lazy(() => import('@/components/ProfitMarginCalculator.jsx'));
const ProductComparator = lazy(() => import('@/components/ProductComparator.jsx'));
const VideoSection = lazy(() => import('@/components/VideoSection.jsx'));
const GuaranteeSection = lazy(() => import('@/components/GuaranteeSection.jsx'));
const TestimonialsSection = lazy(() => import('@/components/TestimonialsSection.jsx'));

const HiperpanLandingPage = () => {
  const { isAdmin, content, editMode, toggleEditMode, saveContent, resetContent } = useEditContext();
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('comparatorBarChange', { detail: { active: selectedProductIds.length > 0 } }));
  }, [selectedProductIds]);

  const toggleProductComparison = (id) => {
    setSelectedProductIds(prev => 
      prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
    );
  };

  const orderedSegmentCards = useMemo(() => {
    const cards = [];
    const usedSegmentIndices = new Set();

    const findSegmentCard = (keywords) => {
      const idx = content.benefits.cards.findIndex((c, i) => {
        if (usedSegmentIndices.has(i)) return false;
        const title = (c.title || '').toLowerCase();
        return keywords.some(kw => title.includes(kw));
      });
      if (idx !== -1) {
        usedSegmentIndices.add(idx);
        return { card: content.benefits.cards[idx], originalIndex: idx };
      }
      return null;
    };

    const padarias = findSegmentCard(['padaria']);
    const confeitarias = findSegmentCard(['confeitaria', 'doce']);
    const restaurantes = findSegmentCard(['restaurante']);
    const cafeterias = findSegmentCard(['cafeteria', 'café', 'cafe']);

    if (padarias) cards.push({ ...padarias, overrides: { result: "Aumentam faturamento em 35-45%", ctaText: "SOLICITAR PROPOSTA PARA PADARIAS", id: "btn-segmento-padaria" } });
    if (confeitarias) cards.push({ ...confeitarias, overrides: { result: "Aumentam ticket médio em 60%", ctaText: "SOLICITAR CATÁLOGO DE DOCES", id: "btn-segmento-confeitaria" } });
    if (restaurantes) cards.push({ ...restaurantes, overrides: { result: "Redução de até 30% no desperdício de estoque", ctaText: "SOLICITAR PROPOSTA PARA RESTAURANTES", id: "btn-segmento-restaurante" } });
    if (cafeterias) cards.push({ ...cafeterias, overrides: { result: "Aumentam acompanhamentos em 50%", ctaText: "SOLICITAR TABELA PARA CAFETERIAS", id: "btn-segmento-cafeteria" } });

    content.benefits.cards.forEach((card, i) => {
      if (!usedSegmentIndices.has(i)) cards.push({ card, originalIndex: i, overrides: {} });
    });

    return cards;
  }, [content.benefits.cards]);

  const productsData = useMemo(() => content.portfolio.products.map((p, i) => ({
    id: `prod-${i}`,
    title: p.title,
    margem: p.margem,
    rotatividade: p.rotatividade,
    beneficio: p.beneficio,
    badge: p.badge
  })), [content.portfolio.products]);

  const transformedComparisonRows = useMemo(() => content.comparison.rows.map(row => {
    if (row.hiperpan === '24/7 VIP') {
      return { ...row, hiperpan: 'Atendimento VIP' };
    }
    return row;
  }), [content.comparison.rows]);

  const faqs = [
    { q: "Como funciona a entrega?", a: "Trabalhamos com logística própria e garantimos entrega em 24h para todas as cidades do Rio Grande do Sul." },
    { q: "Qual é o valor mínimo de pedido?", a: "A partir de R$ 500. Acima desse valor, o frete é por nossa conta em todo o Rio Grande do Sul." },
    { q: "Vocês fazem entregas para fora do RS?", a: "No momento, nossa operação logística é focada exclusivamente no estado do Rio Grande do Sul para garantir a qualidade e o prazo de 24h." },
    { q: "Como faço para solicitar o catálogo?", a: "É simples: clique em qualquer botão de WhatsApp na página e fale diretamente com um de nossos consultores." }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://hiperpancongelados.com.br" },
      { "@type": "ListItem", "position": 2, "name": "Produtos", "item": "https://hiperpancongelados.com.br/produtos" },
      { "@type": "ListItem", "position": 3, "name": "Pães e Doces Congelados", "item": "https://hiperpancongelados.com.br/paes-e-doces-congelados" }
    ]
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Hiperpan",
    "url": "https://hiperpancongelados.com.br",
    "logo": "https://hiperpancongelados.com.br/logo.png",
    "description": "Pães, doces e salgados congelados prontos para vender.",
    "sameAs": [
      "https://instagram.com/hiperpan",
      "https://youtube.com/hiperpan",
      "https://facebook.com/hiperpan",
      "https://linkedin.com/company/hiperpan"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+55-51-99542-0336",
      "contactType": "customer service",
      "email": "contato@hiperpancongelados.com.br"
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "R. Acilino Francisco de Medeiros, 600 - Distrito Industrial",
      "addressLocality": "Gravataí",
      "addressRegion": "RS",
      "postalCode": "94045-410",
      "addressCountry": "BR"
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background relative overflow-hidden">
      <Helmet>
        <title>Hiperpan | Pão Congelado para Todo o Rio Grande do Sul - Entrega 24h</title>
        <meta name="description" content="Pães, doces e salgados congelados prontos para vender. Aumente seus lucros em até 40% sem desperdício. Entrega em 24h no RS. Solicite catálogo!" />
        <meta property="og:title" content="Pães e Doces Congelados Prontos para Vender - Hiperpan" />
        <meta property="og:description" content="Aumente seus lucros em até 40% com produtos de padrão industrial. Entrega em 24h no RS." />
        <meta property="og:image" content="https://hiperpancongelados.com.br/og-image.jpg" />
        <meta property="og:url" content="https://hiperpancongelados.com.br" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pães e Doces Congelados Prontos para Vender - Hiperpan" />
        <meta name="twitter:description" content="Aumente seus lucros em até 40% com produtos de padrão industrial. Entrega em 24h no RS." />
        <meta name="twitter:image" content="https://hiperpancongelados.com.br/og-image.jpg" />
        
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              "name": "Hiperpan",
              "description": "Fornecedor de pão francês, pão de queijo e doces congelados para revenda",
              "url": "https://hiperpancongelados.com.br",
              "areaServed": "RS",
              "serviceArea": {
                "@type": "State",
                "name": "Rio Grande do Sul"
              },
              "priceRange": "$$"
            }
          `}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(orgSchema)}
        </script>
      </Helmet>

      {isAdmin && (
        <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
          {!editMode ? (
            <button onClick={toggleEditMode} className="bg-black text-white p-3 rounded-full shadow-2xl hover:scale-105 transition-transform flex items-center justify-center min-h-[44px] min-w-[44px]" title="Editar Conteúdo" aria-label="Editar Conteúdo"><Edit3 className="w-5 h-5" /></button>
          ) : (
            <div className="bg-white p-4 rounded-xl shadow-2xl border border-border flex flex-col gap-3">
              <div className="text-sm font-bold text-center">Modo Edição Ativo</div>
              <button onClick={saveContent} className="bg-emerald-500 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-emerald-600 transition-colors min-h-[44px]"><Save className="w-4 h-4" /> Salvar</button>
              <button onClick={resetContent} className="bg-destructive/10 text-destructive px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-destructive/20 transition-colors min-h-[44px]"><X className="w-4 h-4" /> Restaurar</button>
            </div>
          )}
        </div>
      )}

      <Header />

      <main className="flex-grow">
        
        {/* 1. HERO SECTION - LIGHT (Overriden by hero-bg) */}
        <div className="bg-section-light">
          <HeroSection />
        </div>

        {/* 1.5 SOLUTIONS SECTION - LIGHT */}
        <div className="bg-section-light">
          <SolutionsSection />
        </div>

        {/* 2. TRUST PROOF SECTION - DARK */}
        <div className="bg-section-dark">
          <TrustProofSection />
        </div>

        {/* 3. STATS SECTION - LIGHT */}
        <div className="bg-section-light">
          <StatsSection />
        </div>

        {/* 4. VIDEO SECTION - DARK */}
        <div className="bg-section-dark">
          <Suspense fallback={<div className="w-full h-[400px] animate-pulse" />}>
            <VideoSection />
          </Suspense>
        </div>

        {/* 5. GUARANTEE SECTION - LIGHT */}
        <div className="bg-section-light">
          <Suspense fallback={<div className="w-full h-[500px] animate-pulse" />}>
            <GuaranteeSection />
          </Suspense>
        </div>

        {/* 6. TESTIMONIALS SECTION - DARK */}
        <div className="bg-section-dark">
          <Suspense fallback={<div className="w-full h-[600px] animate-pulse" />}>
            <TestimonialsSection />
          </Suspense>
        </div>

        {/* 7. BENEFITS SECTION (Problem/Solution) - LIGHT */}
        <div className="bg-section-light">
          <section className="py-16 md:py-24 overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/5 pointer-events-none"></div>
            <div className="container-custom relative z-10">
              <div className="text-center mb-10 md:mb-16">
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-balance">Reduza os Custos da Panificação Tradicional</h2>
              </div>

              <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-6 md:gap-8 items-stretch">
                <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-sm border-l-4 md:border-l-8 border-l-destructive text-slate-900">
                  <h3 className="font-bold text-lg md:text-xl text-destructive mb-6 flex items-center gap-2">Desafios Comuns da Operação</h3>
                  <ul className="space-y-4 md:space-y-6">
                    {[{ icon: AlertCircle, text: "Desperdício de matéria-prima e produtos vencidos" }, { icon: Users, text: "Necessidade de mão de obra especializada e cara" }, { icon: TrendingDown, text: "Altos custos operacionais corroendo a margem" }, { icon: Clock, text: "Produção lenta e inconsistente" }].map((p, i) => (
                      <li key={i} className="flex items-start gap-3 md:gap-4">
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-destructive/10 flex items-center justify-center shrink-0 mt-0.5"><p.icon className="w-4 h-4 md:w-5 md:h-5 text-destructive" /></div>
                        <span className="font-body text-sm md:text-base font-medium leading-relaxed">{p.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="hidden lg:flex flex-col items-center justify-center relative w-16">
                  <div className="absolute inset-0 flex items-center justify-center"><div className="w-px h-full bg-border"></div></div>
                  <div className="bg-primary text-primary-foreground font-bold p-3 md:p-4 rounded-full shadow-xl z-10 text-center flex flex-col items-center gap-1 md:gap-2">
                    <ArrowRight className="w-6 h-6 md:w-8 md:h-8" />
                  </div>
                </div>

                <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-xl border-l-4 md:border-l-8 border-l-primary text-slate-900">
                  <h3 className="font-bold text-lg md:text-xl text-primary mb-6 flex items-center gap-2">A Solução Hiperpan</h3>
                  <ul className="space-y-4 md:space-y-6">
                    {[{ icon: CheckCircle2, text: "Produtos prontos para venda, sem desperdício" }, { icon: Zap, text: "Reduz drasticamente a necessidade de mão de obra" }, { icon: TrendingUp, text: "Margem comercial competitiva de 35% a 50%" }, { icon: Truck, text: "Entrega rápida em 24h para todo o RS" }].map((s, i) => (
                      <li key={i} className="flex items-start gap-3 md:gap-4">
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5"><s.icon className="w-4 h-4 md:w-5 md:h-5 text-primary" /></div>
                        <span className="font-body text-sm md:text-base font-bold leading-relaxed">{s.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-12 md:mt-20 text-center flex justify-center w-full">
                <button 
                  data-gtm="whatsapp"
                  onClick={() => handleWhatsAppClick('Olá Hiperpan! Gostaria de conversar sobre como vocês podem ajudar meu negócio a reduzir custos e aumentar as vendas.')}
                  className="gtm-whatsapp cta-button btn-primary-cro animate-pulse-glow sm:w-auto w-full"
                >
                  <WhatsAppIcon className="cta-icon" /> 
                  <span className="truncate">FALAR COM CONSULTOR</span> 
                  <ArrowRight className="cta-arrow" />
                </button>
              </div>
            </div>
          </section>
        </div>

        {/* 8. PORTFOLIO SECTION - DARK */}
        <div className="bg-section-dark">
          <section className="py-16 md:py-24">
            <div className="container-custom">
              <div className="text-center mb-10 md:mb-16">
                <EditableField section="portfolio" field="title" value="Produtos de Alta Rotatividade" as="h2" className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-balance" />
                <EditableField section="portfolio" field="subtitle" value="Abasteça seu estabelecimento com o mix ideal para atrair clientes diariamente e aumentar seu ticket médio." as="p" className="font-body text-base md:text-lg opacity-80 max-w-2xl mx-auto text-balance" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-stretch mb-12 md:mb-16">
                {content.portfolio.products.map((product, i) => {
                  let image = product.image;
                  let altText = product.title;
                  let badge = product.badge;
                  let margem = product.margem;
                  let rotatividade = product.rotatividade;
                  let beneficio = product.beneficio;
                  let exemplo = product.exemplo;
                  let ctaText = product.ctaText;

                  if (i === 0) {
                    image = produtoPaoFrances; altText = 'Pão francês congelado'; badge = 'MAIS VENDIDO'; margem = '40%'; rotatividade = 'Diária (Alto Giro)'; beneficio = 'Garante fluxo constante de clientes na sua loja'; exemplo = 'Jésus B.: "Empresa super organizada, tem um mix muito grande de produtos de muita qualidade... entrega sempre muito rápida e produtos que chegam com qualidade."'; ctaText = 'SOLICITAR ORÇAMENTO';
                  } else if (i === 1) {
                    image = produtoPaoDeQueijo; altText = 'Pão de queijo premium'; badge = 'FAVORITO DOS CLIENTES'; margem = '65%'; rotatividade = 'Diária'; beneficio = 'Alta compra por impulso no caixa'; exemplo = 'Alcione R.: "Super recomendo! Os produtos da Hiperpan são ótimos, o melhor cacetinho e os melhores salgados folhados da região, sem falar no pão de queijo!"'; ctaText = 'SOLICITAR ORÇAMENTO';
                  } else if (i === 2) {
                    image = produtoDocesFinos; altText = 'Doces finos gourmet'; badge = 'PRODUTO PREMIUM'; margem = '50%'; rotatividade = 'Fim de Semana'; beneficio = 'Aumenta o ticket médio em 20%'; exemplo = 'Roberta A.: "Produtos de alta qualidade e um time de diretores sócios apaixonado pelo negócio."'; ctaText = 'SOLICITAR ORÇAMENTO';
                  }

                  return (
                    <ProductCard 
                      key={i} id={`prod-${i}`} section="portfolio" fieldPrefix={`products.${i}`} {...product} 
                      image={image} altText={altText} badge={badge} margem={margem} rotatividade={rotatividade} beneficio={beneficio} exemplo={exemplo} ctaText={ctaText} delay={i * 0.1}
                      isSelected={selectedProductIds.includes(`prod-${i}`)} onToggleCompare={toggleProductComparison}
                    />
                  );
                })}
              </div>
              
              <Suspense fallback={null}>
                <ProductComparator selectedIds={selectedProductIds} productsData={productsData} onClear={() => setSelectedProductIds([])} />
              </Suspense>
            </div>
          </section>
        </div>

        {/* 9. CALCULATOR SECTION - LIGHT */}
        <div className="bg-section-light">
          <section className="py-12 md:py-20 overflow-hidden">
            <div className="container-custom">
              <div className="max-w-5xl mx-auto">
                <Suspense fallback={<div className="w-full h-[520px] md:h-[480px] rounded-2xl bg-muted animate-pulse" />}>
                  <ProfitMarginCalculator />
                </Suspense>
              </div>
            </div>
          </section>
        </div>

        {/* 10. SEGMENT BENEFITS SECTION - DARK */}
        <div className="bg-section-dark">
          <section id="institucional" className="py-16 md:py-24">
            <div className="container-custom">
              <div className="text-center mb-10 md:mb-16">
                <EditableField section="benefits" field="title" value={content.benefits.title} as="h2" className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-balance" />
              </div>

              <div className="grid md:grid-cols-2 gap-6 md:gap-8 lg:gap-12">
                {orderedSegmentCards.map((item, displayIndex) => (
                  <SegmentCard 
                    key={item.originalIndex} section="benefits" fieldPrefix={`cards.${item.originalIndex}`} {...item.card} 
                    ctaText={item.overrides.ctaText || item.card.ctaText} result={item.overrides.result || item.card.result} id={item.overrides.id || `btn-segmento-${item.originalIndex}`} delay={displayIndex * 0.1} 
                  />
                ))}
              </div>
            </div>
          </section>
        </div>

        {/* 11. DIFFERENTIALS/COMPARISON SECTION - LIGHT */}
        <div className="bg-section-light">
          <section className="py-16 md:py-24">
            <div className="container-custom">
              <div className="text-center mb-10 md:mb-16">
                <EditableField section="comparison" field="title" value={content.comparison.title} as="h2" className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-balance" />
                <EditableField section="comparison" field="subtitle" value="Por que fornecedores comuns limitam o crescimento do seu negócio?" as="p" className="font-body text-base md:text-lg opacity-80 text-balance mx-auto max-w-2xl" />
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block max-w-4xl mx-auto mb-16 bg-card border border-border rounded-2xl shadow-lg overflow-hidden text-card-foreground">
                <div className="w-full">
                  <div className="grid grid-cols-3 md:grid-cols-4 p-4 md:p-6 bg-secondary text-secondary-foreground border-b border-border">
                    <div className="col-span-1 font-bold uppercase text-xs md:text-sm tracking-wider">Benefício</div>
                    <div className="col-span-1 text-center font-bold text-primary uppercase text-xs md:text-sm tracking-wider">Hiperpan</div>
                    <div className="col-span-1 text-center font-bold text-white/60 uppercase text-xs md:text-sm tracking-wider">Comuns</div>
                    <div className="hidden md:block col-span-1 text-center font-bold text-white/60 uppercase text-xs md:text-sm tracking-wider">Atacados</div>
                  </div>
                  {transformedComparisonRows.map((row, i) => (
                    <ComparisonRow key={i} section="comparison" fieldPrefix={`rows.${i}`} {...row} delay={i * 0.05} />
                  ))}
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden grid grid-cols-1 gap-4 mb-10">
                {transformedComparisonRows.map((row, i) => (
                  <div key={i} className="bg-card border border-border rounded-xl p-4 sm:p-5 shadow-sm text-card-foreground">
                    <h3 className="font-bold text-sm mb-3 text-center border-b border-border pb-2">{row.criteria}</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center bg-primary/5 p-2 sm:p-3 rounded-lg border border-primary/20">
                        <span className="text-[10px] sm:text-xs font-bold text-primary uppercase">Hiperpan</span>
                        <span className="text-xs sm:text-sm font-medium text-right">{row.hiperpan}</span>
                      </div>
                      <div className="flex justify-between items-center bg-muted p-2 sm:p-3 rounded-lg">
                        <span className="text-[10px] sm:text-xs font-bold text-muted-foreground uppercase">Comuns</span>
                        <X className="w-4 h-4 text-destructive opacity-60 shrink-0" aria-hidden="true" />
                      </div>
                      <div className="flex justify-between items-center bg-muted p-2 sm:p-3 rounded-lg">
                        <span className="text-[10px] sm:text-xs font-bold text-muted-foreground uppercase">Atacados</span>
                        <X className="w-4 h-4 text-destructive opacity-60 shrink-0" aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center flex justify-center w-full">
                <button
                  data-gtm="whatsapp"
                  onClick={() => handleWhatsAppClick('Olá Hiperpan! Gostaria de solicitar a tabela comercial atualizada de vocês.')}
                  className="gtm-whatsapp cta-button btn-primary-cro animate-pulse-glow sm:w-auto w-full"
                >
                  <WhatsAppIcon className="cta-icon" />
                  <span className="truncate">SOLICITAR TABELA COMERCIAL</span>
                  <ArrowRight className="cta-arrow" />
                </button>
              </div>
            </div>
          </section>
        </div>

        {/* 13. FAQ SECTION - LIGHT */}
        <div className="bg-section-light">
          <section className="py-16 md:py-24">
            <div className="container-custom">
              <div className="max-w-3xl mx-auto">
                <EditableField section="faq" field="title" value={content.faq.title} as="h2" className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-center text-balance" />
                <EditableField section="faq" field="subtitle" value={content.faq.subtitle} as="p" className="font-body text-center text-base md:text-lg opacity-80 mb-8 md:mb-12 block w-full text-balance" />
                
                <Accordion type="single" collapsible className="w-full space-y-3 md:space-y-4">
                  {faqs.map((faq, i) => (
                    <AccordionItem key={i} value={`item-${i}`} className="bg-card border border-border rounded-xl px-4 md:px-6 data-[state=open]:shadow-md transition-all text-card-foreground">
                      <AccordionTrigger className="font-body font-bold text-left text-base md:text-lg hover:no-underline py-4 md:py-5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">{faq.q}</AccordionTrigger>
                      <AccordionContent className="font-body opacity-80 text-sm md:text-base leading-relaxed pb-4 md:pb-6">{faq.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </section>
        </div>

      </main>

      {/* 14. FOOTER */}
      <Footer />
      
      {/* Floating WhatsApp Button positioned globally */}
      <WhatsAppButton />
    </div>
  );
};

export default HiperpanLandingPage;